import { Country, State } from "country-state-city";

// Shared option lists and per-form field definitions for the four public forms.

export const GENRES = [
  "Hip-hop / Rap", "R&B", "Soul", "Pop", "K-pop", "J-pop", "Afrobeats", "Amapiano", "Reggaeton",
  "Latin", "Reggae / Dancehall", "Drill", "Trap", "House", "Electronic", "Alternative", "Indie",
  "Rock", "Country", "Jazz", "Gospel", "Christian", "Desi / South Asian", "Instrumental / Beats", "Other",
];

export const HELP_AREAS = [
  ["Artist Development", ["Music production", "Vocal coaching", "Songwriting development", "Media training", "Dance", "Choreography", "Performance coaching", "Stage presence", "Recording / studio time", "Mixing / mastering", "Creative direction", "Artist identity & positioning"]],
  ["Marketing & Promotions", ["Paid ads", "Social media", "Content strategy", "Email marketing", "SMS marketing", "Radio", "PR", "Playlist promotion", "Influencer / creator campaigns", "Release strategy", "Branding", "Photography / video"]],
  ["Business Support", ["Accounting", "Taxes", "Song / royalty administration", "Publishing", "Registrations (PRO, ISRC, UPC)", "Contracts", "Splits & agreements", "Trademark / legal referrals", "Artist management", "Booking", "Budgeting & financial planning", "Administrative support"]],
];

export const PARTNER_TYPES = [
  "School", "College / University", "Venue", "Record Label", "Brand Partner", "Nonprofit",
  "Church / Faith Organization", "Studio", "Media Company", "Artist Service Provider",
  "Talent / Management Agency", "Sponsor", "Music Program / Academy", "Community Organization",
  "Music-Tech / Platform", "Other",
];

export const TALENT_ROLES = [
  "Musician / Instrumentalist", "Recording Artist / Vocalist", "DJ", "Producer / Beatmaker",
  "Songwriter / Topliner", "Audio Engineer (Mix / Master)", "Recording Engineer", "Vocal Coach",
  "Dance Coach / Choreographer", "Performance Coach", "Artist Manager", "Booking Agent", "A&R",
  "Artist Marketing / Promotions", "Social Media / Content Manager", "Publicist / PR", "Photographer",
  "Videographer / Editor", "Graphic Designer", "Creative Director", "Fashion Designer", "Stylist",
  "Hair / Makeup Artist", "Music Attorney", "Accountant / Royalty Admin", "Tour / Road Crew",
  "Studio Owner", "Other",
];

const MUSIC_ROLES = new Set([
  "Musician / Instrumentalist", "Recording Artist / Vocalist", "DJ", "Producer / Beatmaker",
  "Songwriter / Topliner", "Audio Engineer (Mix / Master)", "Recording Engineer",
]);

export const INDUSTRY_ROLES = [
  "Record Label", "Artist Manager", "A&R", "Music Publisher", "Management Company",
  "Booking Agency", "Distributor", "Entertainment Attorney", "Other",
];

export const ARTIST_COUNTS = ["Just one", "2–5", "6–10", "11–25", "25+"];

const isMusicTalent = (a) => (a.talent_roles || []).some((r) => MUSIC_ROLES.has(r));

const COUNTRIES = Country.getAllCountries();
const COUNTRY_NAMES = COUNTRIES.map((c) => c.name);
const ISO_BY_NAME = Object.fromEntries(COUNTRIES.map((c) => [c.name, c.isoCode]));

export const statesFor = (countryName) => {
  const iso = ISO_BY_NAME[countryName];
  if (!iso) return [];
  return State.getStatesOfCountry(iso).map((s) => s.name);
};

const LOCATION = [
  { id: "country", label: "Country", mode: "Choose one", type: "select", required: true, half: true,
    searchable: true, options: COUNTRY_NAMES },
  { id: "state", label: "State / Province / Region", mode: "Choose one", type: "select", required: true,
    half: true, searchable: true,
    options: (a) => statesFor(a.country),
    cond: (a) => statesFor(a.country).length > 0 },
  { id: "city", label: "City", type: "text", half: true, help: "Optional." },
];

const NAME = { id: "name", label: "Your name", type: "text", required: true, half: true };
const EMAIL = { id: "email", label: "Email address", type: "email", required: true, half: true };
const PHONE = { id: "phone", label: "Phone number", type: "text", required: true, half: true };
const SOCIALS = {
  id: "socials", label: "Social media", type: "pairs", addLabel: "Add Another Profile",
  options: ["Instagram", "TikTok", "YouTube", "X / Twitter", "Threads", "Facebook", "Spotify", "SoundCloud", "LinkedIn", "Other"],
};
const GENRE_OTHER = {
  id: "genre_other", label: "What genre?", type: "text", required: true,
  cond: (a) => (a.genres || []).includes("Other"),
};

export const FORMS = {
  artist: {
    slug: "artist",
    inquiryType: "Artist / Creator",
    eyebrow: "Artists & Creators",
    title: "Work with Sabrea Soundworks.",
    intro: "For artists and creators looking for development, guidance, resources or the right team around their career.",
    scheduler: true,
    fields: [
      NAME, EMAIL, { ...PHONE, half: false }, ...LOCATION,
      { id: "genres", label: "Genres", mode: "Select all that apply", type: "multiselect", options: GENRES },
      GENRE_OTHER,
      { id: "help_areas", label: "What areas are you looking to get help in?", mode: "Select all that apply", type: "grouped", required: true, groups: HELP_AREAS },
      { id: "links", label: "Links", type: "links",
        help: "Music, portfolio, website, press — Spotify, Apple Music, YouTube, SoundCloud, Drive, anything." },
      SOCIALS,
      { id: "message", label: "How else can we help?", type: "longtext", required: true,
        help: "Tell us what you need — a few lines is plenty." },
    ],
  },

  industry: {
    slug: "industry",
    inquiryType: "Label, Manager or A&R",
    eyebrow: "Labels, Managers & A&R",
    title: "Support your roster with Sabrea Soundworks",
    intro: "For labels, managers, A&R, publishers and agencies working with artists who need development, services or a team around them.",
    fields: [
      NAME,
      { id: "industry_role", label: "What's your role?", mode: "Choose one", type: "select", required: true, half: true, options: INDUSTRY_ROLES },
      { id: "industry_role_other", label: "Tell us your role", type: "text", required: true,
        cond: (a) => a.industry_role === "Other" },
      { id: "organization", label: "Company or label name", type: "text", required: true, half: true },
      EMAIL, PHONE,
      { id: "artist_count", label: "How many artists do you represent?", mode: "Choose one", type: "select", required: true, half: true, options: ARTIST_COUNTS },
      { id: "genres", label: "Genres", mode: "Select all that apply", type: "multiselect", options: GENRES },
      GENRE_OTHER,
      { id: "website", label: "Website", type: "text", half: true, help: "Your company or roster page." },
      { id: "links", label: "Roster or artist links", type: "links",
        help: "Spotify, Apple Music, YouTube, press — one per artist is plenty." },
      SOCIALS,
      { id: "message", label: "What are you looking to do with Sabrea Soundworks?", type: "longtext", required: true,
        help: "A few lines is plenty." },
    ],
  },

  partner: {
    slug: "partner",
    inquiryType: "Partnership",
    eyebrow: "Partnerships",
    title: "Partner with Sabrea Soundworks.",
    intro: "For companies, organizations, government agencies, schools, nonprofits, studios, showcases, platforms and communities.",
    fields: [
      { id: "organization", label: "Organization name", type: "text", required: true, half: true },
      { id: "name", label: "Your name", type: "text", required: true, half: true },
      EMAIL, PHONE,
      { id: "partner_type", label: "What kind of organization are you?", mode: "Select all that apply", type: "multiselect", required: true, options: PARTNER_TYPES },
      { id: "partner_type_other", label: "Tell us what kind of organization you are", type: "text", required: true,
        cond: (a) => (a.partner_type || []).includes("Other") },
      { id: "website", label: "Website", type: "text", half: true },
      { id: "message", label: "Why do you want to partner with Sabrea Soundworks?", type: "longtext", required: true,
        help: "A few lines is plenty." },
    ],
  },

  network: {
    slug: "network",
    inquiryType: "Artist Services Network",
    eyebrow: "Artist Services Network",
    title: "Join the Artist Services Network.",
    intro: "For freelance producers, engineers, songwriters, musicians, photographers, videographers, creative directors, stylists, coaches, marketers and other professionals who work with artists.",
    fields: [
      NAME,
      { id: "organization", label: "Company or brand name", help: "Optional.", type: "text", half: true },
      EMAIL, PHONE, ...LOCATION,
      { id: "talent_roles", label: "What services are you interested in providing?", mode: "Select all that apply", type: "multiselect", required: true, options: TALENT_ROLES },
      { id: "talent_role_other", label: "Tell us what you do", type: "text", required: true,
        cond: (a) => (a.talent_roles || []).includes("Other") },
      { id: "genres", label: "Genres", mode: "Select all that apply", type: "multiselect", options: GENRES,
        cond: (a) => isMusicTalent(a) },
      { ...GENRE_OTHER, cond: (a) => isMusicTalent(a) && (a.genres || []).includes("Other") },
      { id: "services", label: "Quick Biography", type: "longtext", required: true,
        help: "Tell us about your experience in the music industry." },
      { id: "resume", label: "Resume", type: "file", required: true,
        accept: ".pdf,.doc,.docx",
        help: "PDF, DOC or DOCX, up to 10 MB." },
      { id: "linkedin", label: "LinkedIn profile", type: "text", half: true,
        help: "Optional." },
      { id: "links", label: "Links", type: "links",
        help: (a) => (isMusicTalent(a) || !(a.talent_roles || []).length)
          ? "Music, portfolio, website, press — Spotify, Apple Music, YouTube, SoundCloud, Drive, anything."
          : "Portfolio, reel, website, work samples — Drive, Behance, YouTube, LinkedIn, anything." },
      SOCIALS,
      { id: "message", label: "Why do you want to join the Artist Services Network?", type: "longtext", required: true,
        help: "A few lines is plenty." },
    ],
  },
};

FORMS.referral = {
  slug: "referral",
  inquiryType: "Referral Network",
  eyebrow: "Referral Network",
  title: "Join the Sabrea Soundworks Referral Network.",
  intro: "Refer artists to Sabrea Soundworks and earn on every one you send our way.",
  fields: [
    NAME, EMAIL, PHONE,
    { id: "talent_roles", label: "What do you do?", mode: "Select all that apply", type: "multiselect", required: true, options: TALENT_ROLES },
    { id: "talent_role_other", label: "Tell us what you do", type: "text", required: true,
      cond: (a) => (a.talent_roles || []).includes("Other") },
    { id: "links", label: "Links", type: "links",
      help: "Website, portfolio, music, anything you'd like us to see." },
    SOCIALS,
  ],
};

export const FORM_LIST = [FORMS.artist, FORMS.industry, FORMS.partner, FORMS.network, FORMS.referral];
